// ── WHICH CAMPAIGN A 578 BELONGS TO ─────────────────────────────────────────
//
// Kevin, 2026-09-25, asked directly: *"A 578 arriving this fall will be CY2027
// wheat (or triticale or barley)."*
//
// So `farmer_578s.tax_year` is the FSA **PROGRAM YEAR the document prints**, and
// the fall campaign runs one crop year AHEAD of the spring one. That settles a
// question the code had been answering two different ways: the Box sync treated
// the program year as authoritative (it forces `extracted === target`), while the
// manual uploader stamped a hardcoded 2026 and both writers guessed the season
// from *today's month*. Neither guess is about the document.
//
// ⚠ TWO CAMPAIGNS ARE LIVE AT ONCE AND THAT IS THE NORMAL STATE. Through the
// autumn, spring-2026 578s are still trickling in while fall-2027 ones start
// arriving. A sync that can only target ONE year has to reject one of them — and
// the rejection was permanent (see boxYearStillRefused).
//
// This module is the one place that answers both questions.
//
// ⚠ IT LIVES IN `src/` AND IS COMMONJS, and both halves of that are deliberate.
// The rule has TWO callers on opposite sides of the wire — `api/box.js` (the
// Box sync) and `src/App.jsx` (the manual uploader) — and they were the two
// writers that disagreed. CRA's module scope forbids `src/` importing from
// `api/`, while `api/` may require from `src/` (the same shape as
// `src/prf/prfGrid.js`). CommonJS is what lets a
// Vercel function require it without a build step.
//
// `src/cropYear.js` holds the ESM year identities and deliberately exports no
// universal "current year", which is why this rule could not simply live there.

// August. The earliest real fall seeding this book has ever recorded is
// 2022-08-16, so a fall campaign is open from 1 August of the year BEFORE its
// crop year. Kept as a named constant because it is the only thing separating a
// fall campaign from the spring one during the overlap.
const FALL_OPENS_MONTH = 8;

// The crop years currently receiving 578s, on a given date.
//
// A spring 578 prints its own calendar year: CY2026 corn is reported in July
// 2026. A fall 578 prints the year AHEAD once drilling starts: wheat drilled in
// September 2026 is the CY2027 crop.
//
// ⚠ THEY COLLAPSE TO ONE YEAR FROM JANUARY TO JULY, and that is correct rather
// than a bug: in June 2027 both the spring campaign being reported and the fall
// campaign still finishing are CY2027. `seasonForProgramYear` reports that
// ambiguity instead of hiding it.
function liveCampaigns(now = new Date()) {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1; // 1-12
  return { spring: y, fall: m >= FALL_OPENS_MONTH ? y + 1 : y };
}

// Every crop year a sync should accept today, newest first.
function acceptedProgramYears(now = new Date()) {
  const { spring, fall } = liveCampaigns(now);
  return [...new Set([fall, spring])].sort((a, b) => b - a);
}

// Which season a program year belongs to, and WHAT THAT ANSWER RESTS ON.
//
// ⚠ THE PROGRAM YEAR ALONE CANNOT ALWAYS DECIDE THIS, and pretending otherwise
// is how the current bug was written. CY2027 has BOTH a fall campaign (wheat
// drilled autumn 2026) and a spring campaign (corn planted spring 2027). While
// the two campaign years differ — August to December, which is exactly when fall
// 578s arrive — the year IS the answer. When they collapse, it is not, and the
// caller is told so via `basis` rather than handed a guess dressed as a fact.
//
// The fallback is the arrival month, which is what both writers used to do
// unconditionally. It is a worse answer and it is now confined to the window
// where nothing better exists.
function seasonForProgramYear(programYear, now = new Date()) {
  const py = Number(programYear);
  const { spring, fall } = liveCampaigns(now);
  if (Number.isFinite(py) && fall !== spring) {
    if (py === fall) return { season: "fall", basis: "program_year" };
    if (py === spring) return { season: "spring", basis: "program_year" };
  }
  // Nothing in the document separates them — fall back to when it arrived, and
  // say so. May-Aug is the spring reporting window; the rest of the year is fall.
  const m = now.getUTCMonth() + 1;
  return { season: m >= 5 && m <= 8 ? "spring" : "fall", basis: "arrival_month" };
}

// The earliest Box file creation date a sync should consider.
//
// ⚠ THE OLD RULE WAS `${targetYear}-05-01` AND IT IS SPRING-SHAPED BY DESIGN —
// its own comment says "the fall-crop / wheat reporting cycle finishes well
// before spring planting" and that "39 stale wheat 578s could fill all 40
// slots". Pointed at a fall campaign it drops the entire campaign: every CY2027
// wheat 578 is created in autumn 2026, months before 2027-05-01.
//
// So the floor is taken from the OLDEST accepted campaign, which keeps the
// spring cutoff exactly where it was while letting a fall campaign's own
// documents through.
function staleCutoffIso(years) {
  const oldest = Math.min(...years.map(Number).filter(Number.isFinite));
  return `${oldest}-05-01T00:00:00Z`;
}

// Should a file already recorded as `wrong_year` stay skipped?
//
// ⚠ THIS IS THE DATA-LOSS FIX. The dedup read `box_scanned_files` with NO filter
// on `decision`, so a file rejected once was skipped forever — including on a
// later run that WOULD have accepted it. A CY2027 wheat 578 arriving while the
// sync targeted only 2026 was therefore lost permanently, and nothing said so.
//
// The cache itself is right and stays: its comment records that it saves about
// $12/week not re-extracting archive PDFs. What changes is that a rejection is
// only honoured while the reason still holds. A 2019 archive print stays
// skipped; a 2027 print becomes eligible the moment 2027 is a live campaign.
//
// Measured 2026-09-25: `box_scanned_files` held 37 rows — 32 duplicate, 5
// not_578, ZERO wrong_year. Nothing had been lost yet.
//
// ⚠ THE DECISION CHECK IS LOAD-BEARING, because a `duplicate` row CARRIES A
// YEAR TOO (`recordScannedFile(file, "duplicate", { decision_year: extractedYear })`).
// Widening this to "any row whose year is live" would reconsider all 32 stored
// duplicates on every run — the cost blowout the cache exists to prevent, in the
// costume of a data-loss fix.
//
// ⚠ AND `Number(null)` IS 0, WHICH IS FINITE. PostgREST returns an explicit
// `null` for an empty column, so a `Number.isFinite` test alone never fires on
// the value that actually arrives — it reached the year comparison instead and
// returned the right answer only because 0 is not a live crop year. That is an
// accident, not a guard. The raw value is tested before it is coerced. (Same
// trap as the crop-year guard in scoEcoBands, recorded in CLAUDE.md.)
function boxYearStillRefused(row, acceptedYears) {
  if (!row || row.decision !== "wrong_year") return true; // not our case — skip as before
  if (row.decision_year == null || row.decision_year === "") return true; // undated — no evidence to reconsider on
  const yr = Number(row.decision_year);
  if (!Number.isFinite(yr)) return true;
  return !acceptedYears.map(Number).includes(yr);
}

module.exports = {
  FALL_OPENS_MONTH,
  liveCampaigns,
  acceptedProgramYears,
  seasonForProgramYear,
  staleCutoffIso,
  boxYearStillRefused,
};
